import { Capacitor } from '@capacitor/core';
import { FirebaseMessaging, Importance } from '@capacitor-firebase/messaging';
import { LocalNotifications } from '@capacitor/local-notifications';
import { notificationsApi } from '../api/client';
import { REFRESH_EVENT } from '../hooks/useListRefresh';

const CHANNEL_ID = 'cat-care-messages';
const APP_MODE = import.meta.env.VITE_APP_MODE;

function isNativeAdminApp(): boolean {
  return Capacitor.isNativePlatform() && APP_MODE === 'admin';
}

let listenersRegistered = false;

/**
 * Sets up foreground/tap/token-refresh listeners and the Android notification
 * channel. Returns a cleanup function.
 */
function registerListeners(): () => void {
  const cleanup: Array<() => void> = [];

  FirebaseMessaging.createChannel({
    id: CHANNEL_ID,
    name: 'CAT CARE Messages',
    description: 'Notifications about purchase and adoption requests',
    importance: Importance.High,
    vibration: true,
    lights: true,
  }).catch((err) => {
    console.warn('[notify] createChannel failed:', err);
  });

  LocalNotifications.registerActionTypes({
    types: [
      {
        id: 'OPEN_REQUEST',
        actions: [
          { id: 'open_purchases', title: 'View Purchases' },
          { id: 'open_adoptions', title: 'View Adoptions' },
        ],
      },
    ],
  }).catch((err) => {
    console.warn('[notify] registerActionTypes failed:', err);
  });

  FirebaseMessaging.addListener('notificationReceived', (event) => {
    console.log('[notify] foreground notification', event.notification);
    const n = event.notification;
    if (!n) return;
    const data = (n.data && typeof n.data === 'object' ? n.data : {}) as Record<string, unknown>;
    const imageUrl = typeof data.imageUrl === 'string' ? data.imageUrl : '';
    LocalNotifications.schedule({
      notifications: [
        {
          id: Math.floor(Date.now() % 2147483647),
          title: typeof n.title === 'string' ? n.title : 'CAT CARE',
          body: typeof n.body === 'string' ? n.body : '',
          channelId: CHANNEL_ID,
          smallIcon: 'ic_stat_icon',
          iconColor: '#0d9569',
          actionTypeId: 'OPEN_REQUEST',
          extra: { type: typeof data.type === 'string' ? data.type : '' },
          ...(imageUrl
            ? { attachments: [{ id: 'img', url: imageUrl }] }
            : { largeIcon: 'ic_notification_large' }),
        },
      ],
    }).catch((err) => {
      console.warn('[notify] foreground local notification failed:', err);
    });
    // Auto-refresh admin lists when a new request arrives.
    window.dispatchEvent(new CustomEvent(REFRESH_EVENT, { detail: { type: data.type } }));
  }).then((h) => cleanup.push(() => h.remove()));

  LocalNotifications.addListener('localNotificationActionPerformed', (event) => {
    console.log('[notify] local action performed', event);
    const actionId = event.actionId;
    const raw = event.notification?.extra as Record<string, unknown> | undefined;
    const type = raw && typeof raw === 'object' ? (typeof raw.type === 'string' ? raw.type : '') : '';
    if (actionId === 'open_purchases' || type === 'purchase_request') {
      window.location.href = '/admin/purchase-requests';
    } else if (actionId === 'open_adoptions' || type === 'adoption_request') {
      window.location.href = '/admin/adoption-requests';
    }
  }).then((h) => cleanup.push(() => h.remove()));

  FirebaseMessaging.addListener('notificationActionPerformed', (event) => {
    const actionId = event.actionId as string | undefined;
    const raw = event.notification?.data;
    const data = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
    const type = typeof data.type === 'string' ? data.type : '';
    if (actionId === 'open_purchases' || type === 'purchase_request') {
      window.location.href = '/admin/purchase-requests';
    } else if (actionId === 'open_adoptions' || type === 'adoption_request') {
      window.location.href = '/admin/adoption-requests';
    }
  }).then((h) => cleanup.push(() => h.remove()));

  FirebaseMessaging.addListener('tokenReceived', (event) => {
    if (event.token) {
      notificationsApi.register({ token: event.token }).catch((err) => {
        console.warn('[notify] refresh token register failed', err);
      });
    }
  }).then((h) => cleanup.push(() => h.remove()));

  listenersRegistered = true;

  return () => {
    cleanup.forEach((fn) => fn());
    listenersRegistered = false;
  };
}

/**
 * Requests permission, fetches the FCM token and registers it with the backend
 * as an admin device. Only runs inside the native admin app.
 */
export async function initAdminNotifications(): Promise<void> {
  if (!isNativeAdminApp()) return;
  if (!listenersRegistered) registerListeners();

  try {
    const perm = await FirebaseMessaging.requestPermissions();
    if (perm.receive !== 'granted') {
      console.warn('[notify] permission not granted');
      return;
    }

    const { token } = await FirebaseMessaging.getToken();
    if (!token) {
      console.warn('[notify] no FCM token');
      return;
    }

    await notificationsApi.register({ token });
  } catch (err) {
    console.warn('[notify] init failed', err);
  }
}
